import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Divider, Grid, InputAdornment, Stack, Typography } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { isEmpty, map } from 'lodash';
import CustomTextInput from '../../../components/CustomTextInput';
import FormSelectField from '../../../components/form/FormSelectField';
import TieredPriceInput from './TieredPriceInput';
import MeterSelect from '../../meters/components/MeterSelect';
import {
  normalizeTiersToMajorAmount,
  normalizeTiersToMinorAmount,
  toMajorAmount,
  toMinorAmount,
} from '../../../utils/currency';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import appConfig from '../../../config/appConfig';

const PRICING_MODEL_OPTIONS = [
  { value: 'licensed', label: 'Flat Rate' },
  { value: 'metered', label: 'Usage Based' },
];

const BILLING_SCHEMA_OPTIONS = [
  { value: 'per_unit', label: 'Per Unit' },
  { value: 'tiered', label: 'Tiered' },
];

const TIERS_MODE_OPTIONS = [
  { value: 'graduated', label: 'Graduated' },
  { value: 'volume', label: 'Volume' },
];

const DEFAULT_TIERS = [{ starting_from: 0, up_to: 'inf', unit_amount: 0, flat_amount: 0 }];

const tierSchema = yup.object({
  starting_from: yup.number().required(),
  up_to: yup.mixed().required(),
  unit_amount: yup.number().min(0, 'Must be ≥ 0').required('Unit amount is required'),
  flat_amount: yup.number().min(0, 'Must be ≥ 0').required('Flat amount is required'),
});

export const buildSchema = (productType) =>
  yup.object().shape({
    name: yup.string().required('Name is required'),
    meter_code: yup
      .string()
      .transform((value) => {
        if (value == null || value === '') {
          return undefined;
        }

        const trimmed = String(value).trim();

        if (trimmed === '') {
          return undefined;
        }

        return trimmed;
      })
      .when(['pricing_model'], {
        is: (pm) => pm === 'metered',
        then: (s) => s.required('Meter is required'),
        otherwise: (s) => s.notRequired().nullable(),
      }),
    pricing_model: yup
      .string()
      .required('Pricing model is required')
      .oneOf(productType === 'prepaid' ? ['licensed'] : ['licensed', 'metered']),
    billing_schema: yup.string().when(['pricing_model'], {
      is: (pm) => pm === 'metered',
      then: (s) => s.oneOf(['per_unit', 'tiered']).required('Billing schema is required for usage-based'),
      otherwise: (s) => s.notRequired().nullable(),
    }),

    tiers_mode: yup.string().when(['billing_schema'], {
      is: (bs) => bs === 'tiered',
      then: (s) => s.oneOf(['graduated', 'volume']).required('Tiers mode is required'),
      otherwise: (s) => s.notRequired().nullable(),
    }),

    unit_amount: yup.mixed().when(['pricing_model', 'billing_schema'], {
      is: (pm, bs) => {
        if (pm === 'licensed' || bs === 'per_unit') {
          return true;
        }
        return false;
      },
      then: () =>
        yup
          .number()
          .typeError('Unit amount must be a number')
          .min(0, 'Must be ≥ 0')
          .required('Unit amount is required'),
      otherwise: () => yup.mixed().notRequired().nullable(),
    }),
    tiers: yup.array().when(['billing_schema'], {
      is: (bs) => bs === 'tiered',
      then: (s) => s.of(tierSchema).min(2, 'At least 2 tiers required').required('Tiers are required'),
      otherwise: (s) => s.notRequired().nullable(),
    }),
    limit: yup
      .number()
      .transform((v, o) => {
        if (o === '' || o === null || o === undefined || o === 'infinite') {
          return 0;
        }

        return v;
      })
      .when(['pricing_model'], {
        is: (pm) => pm === 'metered',
        then: (s) => s.oneOf([0], 'Usage based prices must use unlimited limit').notRequired(),
        otherwise: (s) =>
          s
            .typeError('Limit must be a number')
            .integer('Limit must be a whole number')
            .moreThan(0, 'Limit must be greater than 0')
            .required('Limit is required'),
      }),

    is_active: yup.boolean(),
  });

function defaultValuesFromInitial(initialData) {
  return {
    stripe_product_id: initialData.stripe_product_id || '',
    name: initialData.name || '',
    meter_code: initialData.meter_code || '',
    pricing_model: initialData.pricing_model || '',
    billing_schema: initialData.billing_schema || '',
    tiers_mode: initialData.tiers_mode || '',
    unit_amount:
      initialData.unit_amount !== null && initialData.unit_amount !== undefined
        ? toMajorAmount(initialData.unit_amount)
        : '',
    tiers: initialData.tiers ? normalizeTiersToMajorAmount(initialData.tiers) : DEFAULT_TIERS,
    limit: initialData.limit ?? 0,
    is_active: Boolean(initialData.is_active),
    stripe_price_id: initialData.stripe_price_id || '',
  };
}

function SectionCard({ title, children }) {
  return (
    <>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          letterSpacing: 0.8,
          textTransform: 'uppercase',
          color: 'text.secondary',
          mb: 1.5,
          display: 'block',
        }}
      >
        {title}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {children}
    </>
  );
}

export default function PriceForm({
  onSubmit,
  onCancel,
  initialData = {},
  apiErrors = {},
  readOnly = false,
  isLoading = false,
  isEdit = false,
  hideActions = false,
  productType = 'prepaid',
}) {
  const schema = buildSchema(productType);

  const [tiers, setTiers] = useState(initialData.tiers || DEFAULT_TIERS);

  const {
    control,
    handleSubmit,
    setError,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValuesFromInitial(initialData),
  });

  const pricingModel = watch('pricing_model');
  const billingSchema = watch('billing_schema');
  const limitValue = watch('limit');

  useEffect(() => {
    setValue('billing_schema', '');
    setValue('tiers_mode', '');
    setValue('unit_amount', '');
    setValue('meter_code', '');
  }, [pricingModel, setValue]);

  useEffect(() => {
    setValue('tiers_mode', '');
  }, [billingSchema, setValue]);

  useEffect(() => {
    if (pricingModel === 'metered') {
      setValue('limit', 0, { shouldValidate: true });
    }
  }, [pricingModel, setValue]);

  const isFlatRate = pricingModel === 'licensed';
  const isUsageBased = pricingModel === 'metered';
  const isTiered = isUsageBased && billingSchema === 'tiered';
  const isPerUnit = isUsageBased && billingSchema === 'per_unit';
  const showInfiniteLimit = isUsageBased;

  const showUnitAmount = isFlatRate || isPerUnit;
  const pricingModelOptions = productType === 'prepaid' ? [PRICING_MODEL_OPTIONS[0]] : PRICING_MODEL_OPTIONS;

  useEffect(() => {
    reset(defaultValuesFromInitial(initialData));
    setTiers(initialData.tiers ? normalizeTiersToMajorAmount(initialData.tiers) : DEFAULT_TIERS);
  }, [initialData, reset]);

  useEffect(() => {
    map(apiErrors, (value, key) => setError(key, { message: value, type: 'string' }));
  }, [apiErrors, setError]);

  const handleTiersChange = useCallback(
    (data) => {
      setTiers(data.tiers);
      setValue('tiers', data.tiers, { shouldValidate: true });
    },
    [setValue],
  );

  const handleFormSubmit = (data) => {
    const payload = {
      name: data.name,
      pricing_model: data.pricing_model,
      billing_schema: isEmpty(data.billing_schema) ? null : data.billing_schema,
      meter_code: data.meter_code || null,
      is_active: data.is_active,
      stripe_price_id: data.stripe_price_id || null,
      limit: isUsageBased ? 0 : Number(data.limit),
      tiers: [],
    };

    if (showUnitAmount) {
      payload.unit_amount = toMinorAmount(data.unit_amount);
    }

    if (isTiered) {
      payload.tiers_mode = data.tiers_mode;
      payload.tiers = normalizeTiersToMinorAmount(tiers);
    }

    onSubmit?.(payload);
  };

  const formGrid = (
    <Grid container spacing={2} mt={2}>
      <Grid size={{ xs: 12, md: 6 }}>
        <CustomTextInput name="name" label="Name" control={control} errors={errors} readOnly={readOnly} />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FormSelectField
          name="pricing_model"
          control={control}
          label="Pricing Model"
          readOnly={readOnly}
          disabled={isEdit}
          options={pricingModelOptions}
          errors={errors}
        />
      </Grid>

      <Grid size={{ xs: 12, md: isUsageBased ? 4 : 6 }}>
        <MeterSelect
          name="meter_code"
          control={control}
          label={isUsageBased ? 'Meter' : 'Meter (optional)'}
          readOnly={readOnly}
          disabled={isEdit}
          errors={errors}
        />
      </Grid>

      {isUsageBased && (
        <Grid size={{ xs: 12, md: 4 }}>
          <FormSelectField
            name="billing_schema"
            control={control}
            label="Billing Schema"
            readOnly={readOnly}
            disabled={isEdit}
            options={BILLING_SCHEMA_OPTIONS}
            errors={errors}
          />
        </Grid>
      )}

      {isTiered && (
        <Grid size={{ xs: 12, md: 4 }}>
          <FormSelectField
            name="tiers_mode"
            control={control}
            label="Tiers Mode"
            readOnly={readOnly}
            disabled={isEdit}
            options={TIERS_MODE_OPTIONS}
            errors={errors}
          />
        </Grid>
      )}

      {showUnitAmount && (
        <Grid size={isFlatRate ? { xs: 12, md: 6 } : { xs: 12, md: 4 }}>
          <CustomTextInput
            name="unit_amount"
            label="Unit Amount"
            type="number"
            control={control}
            errors={errors}
            readOnly={readOnly}
            disabled={isEdit}
            adornment={appConfig.stripeCurrency}
          />
        </Grid>
      )}

      <Grid size={isFlatRate ? { xs: 12, md: 6 } : { xs: 12, md: 4 }}>
        <CustomTextInput
          name="limit"
          label="Limit"
          type="text"
          control={control}
          errors={errors}
          readOnly={readOnly}
          disabled={isUsageBased}
          {...(showInfiniteLimit ? { value: 'Unlimited' } : {})}
          slotProps={{
            ...(showInfiniteLimit
              ? {
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <AllInclusiveIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  },
                }
              : {}),
          }}
        />
      </Grid>

      {isTiered && (
        <Grid size={12}>
          <SectionCard title="Tier Pricing">
            <TieredPriceInput formData={{ tiers }} onChange={handleTiersChange} readOnly={readOnly} disabled={isEdit} />
            {errors.tiers && (
              <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                {typeof errors.tiers === 'string' ? errors.tiers : errors.tiers?.message || 'Please fix tier errors'}
              </Typography>
            )}
          </SectionCard>
        </Grid>
      )}
    </Grid>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        px: 2,
        pb: 2,
      }}
    >
      {formGrid}
      {!readOnly && !hideActions && (
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 2, mt: 'auto' }}>
          <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit(handleFormSubmit)} disabled={isLoading}>
            {isEdit ? 'Update' : 'Save'}
          </Button>
        </Stack>
      )}
    </Box>
  );
}
